import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

const inp = 'h-8 px-2 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40';
const thCls = 'text-left px-3 py-2 font-semibold text-sap-textMuted uppercase tracking-wider text-2xs bg-gray-50 border-b border-sap-border';

/* ─────────────── YellowArrow (SAP B1 style) ─────────────── */
const YellowArrow = ({ onClick, title, disabled = false }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    title={title}
    disabled={disabled}
    className={`flex-shrink-0 flex items-center justify-center w-5 h-5 transition-opacity ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80'}`}
  >
    <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
      <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
    </svg>
  </button>
);

function OrderPickerDialog({ open, onClose, onSelect }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const delay = search.trim() === '' ? 0 : 250;
    const t = setTimeout(() => {
      setLoading(true);
      api.get(`/production-orders/receiptable-orders?search=${encodeURIComponent(search)}`)
        .then((d) => {
          setRows(Array.isArray(d) ? d : (d?.rows || d?.data || []));
        })
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, delay);
    return () => clearTimeout(t);
  }, [open, search]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-[min(760px,94vw)] max-h-[70vh] flex flex-col">
        <div className="px-4 py-3 border-b border-sap-border flex items-center justify-between">
          <h3 className="text-sm font-bold text-sap-text">Select Production Order to Receive</h3>
          <button onClick={onClose} className="text-sap-textLight hover:text-red-500 text-lg leading-none">&times;</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input 
            value={search} 
            onChange={e => setSearch(e.target.value)}
            placeholder="Search order #, item…" 
            autoFocus
            className={`w-full ${inp}`} 
          />
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#F0F4F3] sticky top-0">
                <th className="text-right px-2 py-2 font-semibold text-sap-text w-14">ID</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-text">Order #</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-text">Item No.</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-text">Description</th>
                <th className="text-right px-3 py-2 font-semibold text-sap-text">Planned</th>
                <th className="text-right px-3 py-2 font-semibold text-sap-text">Completed</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sap-textLight italic">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sap-textLight italic">No eligible orders found.</td></tr>
              ) : rows.map((o) => (
                <tr 
                  key={o.prod_order_id}
                  onClick={() => onSelect(o)}
                  className="hover:bg-sap-blue/5 cursor-pointer border-b border-sap-border/40"
                >
                  <td className="text-right px-2 py-2 text-sap-textLight font-mono">{o.prod_order_id}</td>
                  <td className="px-3 py-2 font-bold text-sap-blue">{o.order_no}</td>
                  <td className="px-3 py-2 font-semibold">{o.item_no}</td>
                  <td className="px-3 py-2 text-sap-textLight">{o.description}</td>
                  <td className="px-3 py-2 text-right">{Number(o.planned_qty).toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-sap-textLight">{Number(o.completed_qty).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function ReceiptFromProductionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState({
    receipt_number: '',
    series_id: '',
    series: '',
    posting_date: new Date().toISOString().slice(0, 10),
    ref2: '',
    branch: '',
    remarks: '',
    journal_remark: 'Receipt from Production',
    status: 'Draft',
  });

  const [lines, setLines] = useState([]);
  const [receiptSeriesList, setReceiptSeriesList] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId]   = useState(null);
  const [orderPickerOpen, setOrderPickerOpen]     = useState(false);
  const [tab, setTab] = useState('contents'); // 'contents' or 'attachments'

  // Load series for New receipts
  useEffect(() => {
    if (!isNew) return;
    api.get('/production-orders/receipts/series')
      .then((data) => {
        setReceiptSeriesList(data);
        const def = data.find(s => s.is_default);
        if (def) {
          setSelectedSeriesId(def.series_id);
          setForm(prev => ({ ...prev, series_id: def.series_id, series: def.series_name }));
        } else if (data.length > 0) {
          setSelectedSeriesId(data[0].series_id);
          setForm(prev => ({ ...prev, series_id: data[0].series_id, series: data[0].series_name }));
        }
      })
      .catch(() => {});
  }, [isNew]);

  // Load next receipt number when series changes
  useEffect(() => {
    if (!isNew || !selectedSeriesId) return;
    api.get(`/production-orders/receipts/next-number?series_id=${selectedSeriesId}`)
      .then((data) => {
        setForm(prev => ({ ...prev, receipt_number: data.next }));
      })
      .catch(() => {});
  }, [isNew, selectedSeriesId]);

  // Load existing receipt details
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/production-orders/receipts/${id}`)
      .then((data) => {
        setForm({
          receipt_number: data.receipt_number,
          series_id: data.series_id,
          series: data.series,
          posting_date: data.posting_date ? new Date(data.posting_date).toISOString().slice(0, 10) : '',
          ref2: data.ref2 || '',
          branch: data.branch || '',
          remarks: data.remarks || '',
          journal_remark: data.journal_remark || 'Receipt from Production',
          status: data.status,
        });
        setLines(data.lines || []);
      })
      .catch((e) => setError(e.message || 'Failed to load receipt details'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const handleSeriesChange = (seriesIdStr) => {
    const sid = parseInt(seriesIdStr, 10);
    setSelectedSeriesId(sid);
    const matched = receiptSeriesList.find(s => s.series_id === sid);
    setForm(prev => ({
      ...prev,
      series_id: sid,
      series: matched ? matched.series_name : 'Primary'
    }));
  };

  const handleSelectOrder = (order) => {
    setOrderPickerOpen(false);
    if (lines.some(l => l.prod_order_id === order.prod_order_id)) {
      alert('This production order is already added to the receipt lines.');
      return;
    }

    const pendingQty = Math.max(0, Number(order.planned_qty) - Number(order.completed_qty));
    if (pendingQty <= 0) {
      alert(`Order #${order.order_no} has already completed all planned quantities (Planned: ${order.planned_qty}, Completed: ${order.completed_qty}).`);
      return;
    }

    const newLine = {
      prod_order_id: order.prod_order_id,
      order_no: order.order_no,
      prod_order_series: order.series_no || 'Primary',
      line_type: 'Item',
      item_id: order.item_id || null,
      item_no: order.item_no,
      description: order.description,
      trans_type: 'Complete',
      quantity: pendingQty,
      unit_cost: order.unit_cost || 0,
      total_cost: pendingQty * (order.unit_cost || 0),
      warehouse_id: order.warehouse_id,
      wh_code: order.wh_code,
      bin_location: '',
      item_cost: order.unit_cost || 0,
      planned_qty: order.planned_qty,
      completed_qty: order.completed_qty,
      uom_code: order.uom_name || 'NOS',
      uom_name: order.uom_name || 'NOS',
      standort: order.distr_rule || '',
      produktgruppe: 'General',
      by_product1: false,
      zeitraum: '',
      anforderer: '',
      anforderung_bericht: '',
    };

    setLines(prev => [...prev, newLine]);
  };

  const handleLineFieldChange = (idx, field, value) => {
    setLines(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const handleLineQtyChange = (idx, value) => {
    const qty = Math.max(0, Number(value) || 0);
    setLines(prev => {
      const copy = [...prev];
      const line = copy[idx];
      const maxAllowed = Math.max(0, Number(line.planned_qty || 0) - Number(line.completed_qty || 0));

      if (qty > maxAllowed) {
        setError(`Cannot receive ${qty} units for Order #${line.order_no}. Maximum allowed remaining quantity is ${maxAllowed} (Planned: ${line.planned_qty}, Completed: ${line.completed_qty}).`);
      } else {
        setError('');
      }

      copy[idx] = {
        ...line,
        quantity: qty,
        total_cost: qty * Number(line.unit_cost || 0),
      };
      return copy;
    });
  };

  const handleRemoveLine = (idx) => {
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to cancel and delete this receipt? This will revert stock levels.')) return;
    setSaving(true);
    setError('');
    try {
      await api.delete(`/production-orders/receipts/${id}`);
      setSuccessMsg('Receipt deleted successfully.');
      setTimeout(() => navigate('/production/receipts'), 1000);
    } catch (err) {
      setError(err.message || 'Failed to delete receipt');
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lines.length === 0) {
      setError('Please add at least one production order to receive.');
      return;
    }
    if (lines.some(l => l.quantity <= 0)) {
      setError('Quantity must be greater than zero for all lines.');
      return;
    }

    for (const l of lines) {
      const maxAllowed = Math.max(0, Number(l.planned_qty || 0) - Number(l.completed_qty || 0));
      const qty = Number(l.quantity) || 0;
      if (qty > maxAllowed) {
        setError(`Receipt quantity (${qty}) exceeds remaining planned quantity (${maxAllowed}) for Order #${l.order_no} (Planned: ${l.planned_qty}, Completed: ${l.completed_qty}).`);
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        lines: lines.map(l => ({
          prod_order_id: l.prod_order_id,
          quantity: l.quantity,
          unit_cost: l.unit_cost,
          description: l.description,
          warehouse_id: l.warehouse_id,
        })),
      };
      await api.post('/production-orders/receipts', payload);
      setSuccessMsg('Receipt posted successfully.');
      setTimeout(() => navigate('/production/receipts'), 1000);
    } catch (err) {
      setError(err.message || 'Failed to save receipt');
      setSaving(false);
    }
  };

  const fmtAmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totalAmount = lines.reduce((sum, l) => sum + (Number(l.total_cost) || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-sap-textLight text-sm">Loading receipt details…</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Header Panel */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            type="button" 
            onClick={() => navigate('/production/receipts')}
            className="p-1.5 hover:bg-white rounded-lg transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div>
            <h1 className="text-base font-bold text-sap-text">
              {isNew ? 'Receipt from Production – Add' : `Receipt from Production – ${form.receipt_number}`}
            </h1>
            {!isNew && (
              <span className="inline-block mt-0.5 px-2 py-0.5 rounded-full text-2xs font-semibold bg-[#E8F4FE] text-[#2184F2]">
                {form.status}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button 
              type="button" 
              onClick={handleDelete} 
              disabled={saving}
              className="px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Deleting…' : 'Delete/Cancel'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-4 py-2 rounded-lg">{error}</div>
      )}
      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs px-4 py-2 rounded-lg">{successMsg}</div>
      )}

      {/* Form Fields Header */}
      <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4 border border-sap-border">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Number</label>
            <input value={form.receipt_number || '(Auto)'} readOnly className={`w-full ${inp} bg-gray-50 text-sap-textLight`} />
          </div>
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Series</label>
            {isNew ? (
              <div className="relative">
                <select
                  value={selectedSeriesId || ''}
                  onChange={e => handleSeriesChange(e.target.value)}
                  className="w-full h-8 pl-3 pr-8 text-xs border border-sap-border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/20 focus:border-sap-blue appearance-none"
                >
                  {receiptSeriesList.map(s => (
                    <option key={s.series_id} value={s.series_id}>{s.series_name}</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sap-textLight" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            ) : (
              <input value={form.series || 'Primary'} readOnly className={`w-full ${inp} bg-gray-50 text-sap-textLight`} />
            )}
          </div>
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Posting Date</label>
            <input 
              type="date" 
              value={form.posting_date}
              disabled={!isNew}
              onChange={e => setForm({ ...form, posting_date: e.target.value })}
              className={`w-full ${inp} ${!isNew ? 'bg-gray-50' : ''}`} 
            />
          </div>
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Ref. 2</label>
            <input 
              value={form.ref2}
              disabled={!isNew}
              onChange={e => setForm({ ...form, ref2: e.target.value })}
              placeholder="e.g. VISHAL"
              className={`w-full ${inp} ${!isNew ? 'bg-gray-50' : ''}`} 
            />
          </div>
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Branch</label>
            <input 
              value={form.branch}
              disabled={!isNew}
              onChange={e => setForm({ ...form, branch: e.target.value })}
              placeholder="e.g. Arm Welders Pvt.Ltd."
              className={`w-full ${inp} ${!isNew ? 'bg-gray-50' : ''}`} 
            />
          </div>
        </div>
      </div>

      {/* Grid Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-sap-border">
        <div className="flex border-b border-sap-border">
          {['contents', 'attachments'].map(t => (
            <button 
              key={t} 
              type="button"
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 text-xs font-semibold transition-colors capitalize
                ${tab === t ? 'text-sap-blue border-b-2 border-sap-blue bg-sap-blue/5' : 'text-sap-textLight hover:text-sap-text'}`}
            >
              {t === 'contents' ? `Contents (${lines.length})` : 'Attachments'}
            </button>
          ))}
        </div>

        {tab === 'contents' ? (
          <div className="p-4 space-y-4">
            <div className="overflow-x-auto max-w-full">
              <table className="min-w-full text-xs">
                <thead>
                  <tr>
                    <th className={thCls + ' w-12 text-center'}>#</th>
                    {isNew && <th className={thCls + ' w-12 text-center'}>Actions</th>}
                    <th className={thCls}>Order No.</th>
                    <th className={thCls}>Series No.</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Item No.</th>
                    <th className={thCls}>Item Description</th>
                    <th className={thCls}>Trans. Type</th>
                    <th className={thCls + ' text-right'}>Quantity</th>
                    <th className={thCls + ' text-right'}>Unit Price</th>
                    <th className={thCls}>Whse</th>
                    <th className={thCls}>Bin Location Allocation</th>
                    <th className={thCls + ' text-right'}>Item Cost</th>
                    <th className={thCls + ' text-right'}>Planned</th>
                    <th className={thCls + ' text-right'}>Completed</th>
                    <th className={thCls}>UoM Code</th>
                    <th className={thCls}>UoM Name</th>
                    <th className={thCls}>Standort</th>
                    <th className={thCls}>Produktgruppe</th>
                    <th className={thCls + ' text-center'}>ByProduct1</th>
                    <th className={thCls}>Zeitraum</th>
                    <th className={thCls}>Anforderer</th>
                    <th className={thCls}>Anforderung Bericht</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={isNew ? 24 : 23} className="px-3 py-8 text-center text-sap-textLight italic bg-gray-50/50">
                        No lines added. Use the "Production Order" picker at the bottom right to select an order.
                      </td>
                    </tr>
                  ) : lines.map((line, idx) => (
                    <tr key={idx} className="border-b border-sap-border/50 hover:bg-gray-50/30">
                      <td className="px-3 py-2 text-center text-sap-textLight font-mono">{idx + 1}</td>
                      {isNew && (
                        <td className="px-3 py-2 text-center">
                          <button 
                            type="button" 
                            onClick={() => handleRemoveLine(idx)}
                            className="text-red-500 hover:text-red-700 font-semibold"
                          >
                            Remove
                          </button>
                        </td>
                      )}
                      {/* Order No. */}
                      <td className="px-3 py-2 font-bold text-sap-blue">
                        <div className="flex items-center gap-1">
                          <YellowArrow 
                            onClick={() => window.open(`/production-order/${line.prod_order_id}`, '_blank')} 
                            title="Drilldown to Production Order" 
                          />
                          <span>{line.order_no}</span>
                        </div>
                      </td>
                      {/* Series No. */}
                      <td className="px-3 py-2">{line.prod_order_series}</td>
                      {/* Type */}
                      <td className="px-3 py-2 text-sap-textLight">Item</td>
                      {/* Item No. */}
                      <td className="px-3 py-2 font-semibold">
                        <div className="flex items-center gap-1">
                          <YellowArrow 
                            onClick={() => window.open(`/item-master/${line.item_no}`, '_blank')} 
                            title="Drilldown to Item Master" 
                          />
                          <span>{line.item_no}</span>
                        </div>
                      </td>
                      {/* Item Description */}
                      <td className="px-3 py-2 text-sap-textLight max-w-xs truncate">{line.description}</td>
                      {/* Trans. Type */}
                      <td className="px-3 py-2">{line.trans_type || 'Complete'}</td>
                      {/* Quantity */}
                      <td className="px-3 py-2 text-right">
                        {isNew ? (
                          <div className="flex flex-col items-end">
                            <input
                              type="number"
                              step="any"
                              max={Math.max(0, Number(line.planned_qty || 0) - Number(line.completed_qty || 0))}
                              value={line.quantity}
                              onChange={e => handleLineQtyChange(idx, e.target.value)}
                              className={`w-20 text-right p-1 border rounded text-xs focus:ring-1 ${
                                Number(line.quantity) > (Number(line.planned_qty || 0) - Number(line.completed_qty || 0))
                                  ? 'border-red-500 bg-red-50 text-red-700 font-semibold focus:ring-red-500'
                                  : 'border-sap-border focus:ring-sap-blue/20'
                              }`}
                            />
                            {Number(line.quantity) > (Number(line.planned_qty || 0) - Number(line.completed_qty || 0)) && (
                              <span className="text-[10px] text-red-600 mt-0.5 whitespace-nowrap font-medium">
                                Max: {Math.max(0, Number(line.planned_qty || 0) - Number(line.completed_qty || 0))}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span>{Number(line.quantity).toLocaleString()}</span>
                        )}
                      </td>
                      {/* Unit Price */}
                      <td className="px-3 py-2 text-right">₹{fmtAmt(line.unit_cost)}</td>
                      {/* Whse */}
                      <td className="px-3 py-2 font-semibold">
                        <div className="flex items-center gap-1">
                          <YellowArrow 
                            onClick={() => navigate('/admin/setup/warehouses')} 
                            title="Drilldown to Warehouses Setup" 
                          />
                          <span>{line.wh_code || line.warehouse_id}</span>
                        </div>
                      </td>
                      {/* Bin Location Allocation */}
                      <td className="px-3 py-2 text-sap-textLight">
                        <div className="flex items-center gap-1">
                          <YellowArrow onClick={() => {}} title="Bin Location Link" disabled />
                          <span>{line.bin_location || '—'}</span>
                        </div>
                      </td>
                      {/* Item Cost */}
                      <td className="px-3 py-2 text-right font-semibold bg-gray-50/30">₹{fmtAmt(line.item_cost || line.unit_cost)}</td>
                      {/* Planned */}
                      <td className="px-3 py-2 text-right text-sap-textLight">{Number(line.planned_qty || 0).toLocaleString()}</td>
                      {/* Completed */}
                      <td className="px-3 py-2 text-right text-sap-textLight">{Number(line.completed_qty ?? 0).toLocaleString()}</td>
                      {/* UoM Code */}
                      <td className="px-3 py-2 text-sap-textLight">{line.uom_code || line.uom_name || 'NOS'}</td>
                      {/* UoM Name */}
                      <td className="px-3 py-2 text-sap-textLight">{line.uom_name || 'NOS'}</td>
                      {/* Standort */}
                      <td className="px-3 py-2 font-semibold">
                        <div className="flex items-center gap-1">
                          <YellowArrow onClick={() => {}} title="Standort / Distr. Rule Link" disabled />
                          <span>{line.standort || line.distr_rule || '—'}</span>
                        </div>
                      </td>
                      {/* Produktgruppe */}
                      <td className="px-3 py-2 text-sap-textLight">{line.produktgruppe || 'General'}</td>
                      {/* ByProduct1 */}
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={!!line.by_product1} disabled readOnly className="rounded border-gray-300" />
                      </td>
                      {/* Zeitraum */}
                      <td className="px-3 py-2 text-sap-textLight">
                        {isNew ? (
                          <input
                            type="text"
                            value={line.zeitraum || ''}
                            onChange={e => handleLineFieldChange(idx, 'zeitraum', e.target.value)}
                            className="w-24 text-xs p-1 border border-sap-border rounded focus:ring-1 focus:ring-sap-blue/20 bg-white"
                            placeholder="Zeitraum"
                          />
                        ) : (
                          <span>{line.zeitraum || '—'}</span>
                        )}
                      </td>
                      {/* Anforderer */}
                      <td className="px-3 py-2 text-sap-textLight">
                        {isNew ? (
                          <input
                            type="text"
                            value={line.anforderer || ''}
                            onChange={e => handleLineFieldChange(idx, 'anforderer', e.target.value)}
                            className="w-24 text-xs p-1 border border-sap-border rounded focus:ring-1 focus:ring-sap-blue/20 bg-white"
                            placeholder="Anforderer"
                          />
                        ) : (
                          <span>{line.anforderer || '—'}</span>
                        )}
                      </td>
                      {/* Anforderung Bericht */}
                      <td className="px-3 py-2 text-sap-textLight">
                        {isNew ? (
                          <input
                            type="text"
                            value={line.anforderung_bericht || ''}
                            onChange={e => handleLineFieldChange(idx, 'anforderung_bericht', e.target.value)}
                            className="w-28 text-xs p-1 border border-sap-border rounded focus:ring-1 focus:ring-sap-blue/20 bg-white"
                            placeholder="Bericht"
                          />
                        ) : (
                          <span>{line.anforderung_bericht || '—'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Content Actions / Summary */}
            <div className="flex justify-between items-start pt-3 border-t border-sap-border/40">
              <div className="w-1/2 space-y-2">
                <div>
                  <label className="text-xs text-sap-textLight block mb-0.5">Remarks</label>
                  <textarea
                    rows={2}
                    value={form.remarks}
                    disabled={!isNew}
                    onChange={e => setForm({ ...form, remarks: e.target.value })}
                    className="w-full max-w-md border border-sap-border rounded text-xs p-1.5 focus:outline-none"
                    placeholder="Enter document remarks here..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-full max-w-md">
                    <label className="text-xs text-sap-textLight block mb-0.5">Journal Remark</label>
                    <div className="flex items-center gap-1">
                      <YellowArrow 
                        onClick={() => navigate('/inventory/postings')} 
                        title="Drilldown to Inventory Posting List" 
                      />
                      <input
                        value={form.journal_remark}
                        disabled={!isNew}
                        onChange={e => setForm({ ...form, journal_remark: e.target.value })}
                        className={`w-full ${inp} ${!isNew ? 'bg-gray-50' : ''}`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-right space-y-1 pr-4">
                <span className="text-2xs text-sap-textLight block uppercase font-semibold">Total Amount</span>
                <span className="text-lg font-bold text-sap-text">₹{fmtAmt(totalAmount)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-xs text-sap-textLight italic">
            Attachments tab is read-only.
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className="flex justify-between items-center bg-gray-50/50 p-4 border border-sap-border rounded-xl">
        <div className="flex items-center gap-2">
          {isNew ? (
            <button 
              type="submit" 
              disabled={saving}
              className="px-6 h-8 text-xs font-semibold bg-sap-blue text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add'}
            </button>
          ) : (
            <button 
              type="button" 
              onClick={() => navigate('/production/receipts')}
              className="px-6 h-8 text-xs font-semibold bg-sap-blue text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              OK
            </button>
          )}
          <button 
            type="button" 
            onClick={() => navigate('/production/receipts')}
            className="px-4 h-8 text-xs font-semibold border border-sap-border text-sap-text rounded-lg hover:bg-white transition-colors"
          >
            Cancel
          </button>
        </div>

        {isNew && (
          <button
            type="button"
            onClick={() => setOrderPickerOpen(true)}
            className="px-4 h-8 text-xs font-semibold bg-white border border-sap-blue text-sap-blue rounded-lg hover:bg-sap-blue/5 transition-colors"
          >
            Production Order
          </button>
        )}
      </div>

      <OrderPickerDialog 
        open={orderPickerOpen}
        onClose={() => setOrderPickerOpen(false)}
        onSelect={handleSelectOrder}
      />
    </form>
  );
}
